/**
 * m3 echo Frontend - Quellenstatus je Antwort (Zitat-Anteil)
 *
 * Zeigt Auflösungsstatus und Zitatzuordnung je Quelle. Nur http(s)-Ziele sind
 * klickbare Links; alles andere wird als Text ausgegeben.
 */

import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { GroundingSource } from '../types/api';
import { safeHttpUrl } from '../utils/safeUrl';
import { safeText } from '../utils/safeText';

const RESOLUTION_LABELS: Record<string, string> = {
  resolved: 'Domain bestimmt',
  pending: 'wird noch aufgelöst',
  unresolved: 'Domain nicht bestimmbar',
  blocked: 'Auflösung blockiert',
  invalid: 'keine verwertbare URL',
};

const RESOLUTION_COLORS: Record<string, 'default' | 'success' | 'warning'> = {
  resolved: 'success',
  pending: 'warning',
};

const NOT_ATTRIBUTED_TEXT =
  'Quelle gefunden; eine Verwendung als Zitat im Antworttext ist nicht nachgewiesen.';

const evidenceText = (source: GroundingSource): string | null => {
  const status = (source.citation_evidence as { status?: string } | null | undefined)?.status;
  if (status === 'cited') return 'Im Antworttext als Zitat belegt.';
  if (status === 'not_cited' || status === 'unknown') return NOT_ATTRIBUTED_TEXT;
  return null;
};

interface Props {
  sources: GroundingSource[];
}

const CitationSourceList: React.FC<Props> = ({ sources }) => (
  <Box data-testid="citation-source-list">
    {sources.map((source, idx) => {
      const href = safeHttpUrl(source.resolved_url) ?? safeHttpUrl(source.url);
      const label = safeText(source.title) || safeText(source.url);
      const status = source.resolution_status || null;
      const evidence = evidenceText(source);
      const target = source.domain || source.host;
      return (
        <Box key={source.source_id ?? idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <LinkIcon fontSize="small" color="action" sx={{ mt: 0.3, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            {href ? (
              <Typography
                variant="body2"
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main', textDecoration: 'none', wordBreak: 'break-word', '&:hover': { textDecoration: 'underline' } }}
              >
                {label}
                <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {label}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              {target && (
                <Typography variant="caption" color="text.secondary">
                  {target}
                </Typography>
              )}
              {status && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={RESOLUTION_COLORS[status] ?? 'default'}
                  label={RESOLUTION_LABELS[status] ?? status}
                />
              )}
            </Box>
            {evidence && (
              <Typography variant="caption" color="text.secondary" display="block">
                {evidence}
              </Typography>
            )}
            {source.snippet && (
              <Typography variant="caption" color="text.secondary" display="block">
                {source.snippet.substring(0, 100)}{source.snippet.length > 100 ? '...' : ''}
              </Typography>
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);

export default CitationSourceList;
